import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { useAdmins } from '../../lib/admins'
import type { AdminRole, AdminUser, RoleOption, WithTempPassword } from '../../lib/admins'
import { formatUtcAsThai, nowThai } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'
import { ReasonPrompt, SectionHead } from '../offsite/parts'

/**
 * บัญชีผู้ดูแลระบบ — เข้าได้เฉพาะ superadmin (กันไว้ที่ NAV + backend อีกชั้น)
 *
 * ⚠️ รหัสชั่วคราวโผล่ครั้งเดียวหลังกดสร้าง/ตั้งใหม่ ไม่มีทางเรียกดูซ้ำ
 *    (ไม่ได้เก็บไว้ที่ไหนเลย — ในตารางมีแต่แฮช) จึงต้องแสดงเด่นและอยู่จนกว่าจะกดปิด
 */

/** อธิบายว่าแต่ละสิทธิ์ทำอะไรได้จริง — ชื่อ role อย่างเดียวคนตั้งเดาเอาเองไม่ตรงกัน */
const ROLE_NOTE: Record<AdminRole, string> = {
  superadmin: 'ทำได้ทุกอย่าง + จัดการบัญชีผู้ดูแล (หน้านี้)',
  admin: 'จัดการข้อมูลได้ทั้งหมด แต่เข้าหน้านี้ไม่ได้',
  hr: 'แก้ข้อมูลพนักงาน/สิทธิ์ลงเวลาได้ เข้าหน้านี้ไม่ได้',
  viewer: 'ดูอย่างเดียว ทุกปุ่มที่แก้ข้อมูลจะไม่แสดง',
}

export default function Admins() {
  const session = useAuthedSession()
  const admins = useAdmins()
  const meId = session.kind === 'admin' ? session.id : -1

  const [rows, setRows] = useState<AdminUser[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'status', dir: 'asc' }, { key: 'username', dir: 'asc' }], () => setPage(1))
  const [status, setStatus] = useState<'active' | 'disabled' | 'all'>('all')
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')

  const [roles, setRoles] = useState<RoleOption[]>([])
  const [minPassword, setMinPassword] = useState(10)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [loadedAt, setLoadedAt] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [disabling, setDisabling] = useState<AdminUser | null>(null)
  /** รหัสชั่วคราวที่เพิ่งออก — ค้างไว้จนกว่าคนกดจะยืนยันว่าคัดลอกแล้ว */
  const [issued, setIssued] = useState<WithTempPassword | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await admins.list({ status, q, page, pageSize, sort: sort.param })
      setRows(res.users)
      setTotal(res.total)
      setLoadedAt(nowThai())
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [admins, status, q, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let alive = true
    admins
      .roles()
      .then((res) => {
        if (!alive) return
        setRoles(res.roles)
        setMinPassword(res.minPassword)
      })
      .catch(() => {
        // ไม่ได้รายชื่อสิทธิ์ = ฟอร์มสร้างใช้ไม่ได้ แต่ตารางยังอ่านได้ ไม่ต้องล้มทั้งหน้า
      })
    return () => {
      alive = false
    }
  }, [admins])

  /** ทำอะไรสักอย่างแล้วโหลดตารางใหม่ — รวม error handling ไว้ที่เดียว */
  const run = async (work: () => Promise<string>) => {
    setError(null)
    try {
      setNotice(await work())
      await load()
    } catch (err) {
      setNotice(null)
      setError(errorText(err))
    }
  }

  return (
    <>
      <h1>ผู้ดูแลระบบ</h1>

      <p className="meta">
        บัญชีสำหรับเข้าหลังบ้านนี้ — คนละชุดกับบัญชีพนักงานที่ใช้ล็อกอินด้วยรหัสพนักงาน
        และคนละชุดกับการผูก LINE · แก้ชื่อผู้ใช้ภายหลังไม่ได้ เพราะชื่อนี้ถูกบันทึกเป็น
        “ผู้ทำรายการ” ไว้ในประวัติของอีกหลายตาราง
      </p>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {issued && <TempPasswordBox issued={issued} onClose={() => setIssued(null)} />}

      {disabling && (
        <ReasonPrompt
          title="ปิดบัญชีผู้ดูแล"
          detail={`${disabling.username} · ${disabling.displayName ?? '-'} · ${disabling.roleLabel}`}
          confirmLabel="ปิดบัญชีนี้"
          placeholder="เช่น ลาออก / ย้ายแผนก"
          onConfirm={async (reason) => {
            const res = await admins.disable(disabling.id, reason)
            setDisabling(null)
            setNotice(`ปิดบัญชี ${res.user.username} แล้ว — ถ้ากำลังเปิดหน้าเว็บค้างอยู่จะถูกเตะออกทันที`)
            await load()
          }}
          onClose={() => setDisabling(null)}
        />
      )}

      {editing && (
        <UserForm
          key={editing.id}
          user={editing}
          roles={roles}
          minPassword={minPassword}
          onDone={async (message) => {
            setEditing(null)
            setNotice(message)
            await load()
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {!showForm && !editing && (
        <p>
          <button type="button" onClick={() => setShowForm(true)}>
            สร้างบัญชีผู้ดูแล
          </button>
        </p>
      )}

      {showForm && (
        <UserForm
          roles={roles}
          minPassword={minPassword}
          onIssued={setIssued}
          onDone={async (message) => {
            setShowForm(false)
            setNotice(message)
            await load()
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <section className="card">
        <SectionHead
          title="บัญชีทั้งหมด"
          meta={loadedAt ? `ข้อมูลเมื่อ ${loadedAt.slice(11, 16)} (เวลาไทย)` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(term.trim())
          }}
        >
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="ชื่อผู้ใช้ หรือชื่อที่แสดง"
            />
          </label>
          <label>
            สถานะ
            <select
              value={status}
              onChange={(e) => {
                setPage(1)
                setStatus(e.target.value as typeof status)
              }}
            >
              <option value="all">ทั้งหมด</option>
              <option value="active">ใช้งานอยู่</option>
              <option value="disabled">ปิดใช้งาน</option>
            </select>
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {rows === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : rows.length === 0 ? (
          <p className="meta">{q ? `ไม่พบบัญชีที่ตรงกับ “${q}”` : 'ยังไม่มีบัญชี'}</p>
        ) : (
          <>
            <SortBar
              sort={sort.sort}
              labels={{ username: 'ชื่อผู้ใช้', role: 'สิทธิ์', lastLoginAt: 'เข้าล่าสุด', status: 'สถานะ' }}
              onClear={() => sort.setSort([])}
            />
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="ชื่อผู้ใช้" sortKey="username" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="สิทธิ์" sortKey="role" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="เข้าล่าสุด" sortKey="lastLoginAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                    <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                    <th>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className={u.status === 'active' ? undefined : 'off'}>
                      <td>
                        <strong>{u.username}</strong>
                        {u.id === meId && <span className="chip">คุณ</span>}
                        <div className="meta">{u.displayName ?? '-'}</div>
                        {(u.email || u.empCode) && (
                          <div className="meta">
                            {[u.email, u.empCode && `รหัส ${u.empCode}`].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>
                        {u.roleLabel}
                        <div className="meta">{ROLE_NOTE[u.role]}</div>
                      </td>
                      <td className="nowrap">
                        {u.lastLoginAt ? formatUtcAsThai(u.lastLoginAt) : <span className="meta">ยังไม่เคยเข้า</span>}
                      </td>
                      <td>
                        <StatusChips user={u} />
                      </td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="ghost tiny" onClick={() => setEditing(u)}>
                            แก้ไข
                          </button>

                          {/* ปลดล็อกโผล่เฉพาะตอนล็อกจริง — ปุ่มที่กดแล้วไม่เกิดอะไรทำให้คนไม่เชื่อหน้าจอ */}
                          {u.locked && (
                            <button
                              type="button"
                              className="ghost tiny"
                              onClick={() =>
                                void run(async () => {
                                  await admins.unlock(u.id)
                                  return `ปลดล็อก ${u.username} แล้ว`
                                })
                              }
                            >
                              ปลดล็อก
                            </button>
                          )}

                          {u.id !== meId && (
                            <button
                              type="button"
                              className="ghost tiny"
                              onClick={() =>
                                void run(async () => {
                                  const res = await admins.resetPassword(u.id)
                                  setIssued(res)
                                  return `ตั้งรหัสใหม่ให้ ${res.user.username} แล้ว`
                                })
                              }
                            >
                              ตั้งรหัสใหม่
                            </button>
                          )}

                          {u.status === 'active'
                            ? u.id !== meId && (
                                <button
                                  type="button"
                                  className="ghost tiny"
                                  onClick={() => setDisabling(u)}
                                >
                                  ปิดบัญชี
                                </button>
                              )
                            : (
                                <button
                                  type="button"
                                  className="ghost tiny"
                                  onClick={() =>
                                    void run(async () => {
                                      await admins.enable(u.id)
                                      return `เปิดบัญชี ${u.username} แล้ว`
                                    })
                                  }
                                >
                                  เปิดใช้งาน
                                </button>
                              )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPage={setPage}
          unit="บัญชี"
          onPageSize={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </section>
    </>
  )
}

function StatusChips({ user }: { user: AdminUser }) {
  if (user.status !== 'active') {
    return (
      <>
        <span className="chip chip--off">ปิดใช้งาน</span>
        <div className="meta">
          {formatUtcAsThai(user.disabledAt)}
          {user.disableReason ? ` · ${user.disableReason}` : ''}
        </div>
      </>
    )
  }

  return (
    <>
      <span className="chip chip--ok">ใช้งานอยู่</span>
      {user.locked && (
        <>
          <span className="chip chip--warn">ถูกล็อก</span>
          <div className="meta">
            กรอกรหัสผิด {user.failedCount} ครั้ง · ถึง {formatUtcAsThai(user.lockedUntil)}
          </div>
        </>
      )}
      {user.mustChangePassword && (
        <div className="meta">ยังใช้รหัสชั่วคราว — จะถูกบังคับเปลี่ยนตอนเข้าครั้งแรก</div>
      )}
    </>
  )
}

/**
 * กล่องโชว์รหัสชั่วคราว — จงใจให้ปิดยาก (ต้องกดปุ่มเอง ไม่หายเองตามเวลา)
 * เพราะถ้าพลาดไป ทางเดียวคือกดตั้งรหัสใหม่อีกรอบ
 */
function TempPasswordBox({ issued, onClose }: { issued: WithTempPassword; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  return (
    <section className="card warn">
      <h2>รหัสชั่วคราวของ {issued.user.username}</h2>
      <p className="temp-pass mono">{issued.tempPassword}</p>
      <p className="meta">
        <strong>ดูได้ครั้งเดียว</strong> — ระบบเก็บไว้เป็นแฮชเท่านั้น ปิดกล่องนี้แล้วเรียกดูซ้ำไม่ได้
        ต้องกด “ตั้งรหัสใหม่” เพื่อออกรหัสใหม่แทน · เจ้าตัวจะถูกบังคับให้เปลี่ยนรหัสทันทีที่เข้าครั้งแรก
      </p>
      <div className="row-actions">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(issued.tempPassword).then(
              () => setCopied(true),
              // คลิปบอร์ดใช้ไม่ได้ถ้าไม่ได้อยู่บน https — ยังเลือกข้อความเองได้อยู่ดี
              () => setCopied(false),
            )
          }}
        >
          {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
        </button>
        <button type="button" className="ghost" onClick={onClose}>
          ส่งให้เจ้าตัวแล้ว ปิดกล่องนี้
        </button>
      </div>
    </section>
  )
}

function UserForm({
  user,
  roles,
  minPassword,
  onIssued,
  onDone,
  onCancel,
}: {
  /** มี = แก้ไข · ไม่มี = สร้างใหม่ */
  user?: AdminUser
  roles: RoleOption[]
  minPassword: number
  onIssued?: (issued: WithTempPassword) => void
  onDone: (message: string) => Promise<void> | void
  onCancel: () => void
}) {
  const admins = useAdmins()
  const editing = user !== undefined

  const [username, setUsername] = useState(user?.username ?? '')
  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [empCode, setEmpCode] = useState(user?.empCode ?? '')
  const [role, setRole] = useState<AdminRole>(user?.role ?? 'viewer')
  /** ตั้งรหัสเองแทนการสุ่ม — ค่าปกติคือไม่ตั้ง เพราะรหัสที่คนคิดเองมักเดาง่าย */
  const [ownPassword, setOwnPassword] = useState(false)
  const [password, setPassword] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const passwordBad = ownPassword && password.length > 0 && password.length < minPassword
  const canSubmit =
    !busy &&
    (editing || username.trim().length >= 3) &&
    (!ownPassword || password.length >= minPassword)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    setBusy(true)
    setError(null)
    try {
      if (editing) {
        const res = await admins.update(user.id, {
          displayName: displayName.trim(),
          email: email.trim(),
          empCode: empCode.trim(),
          role,
        })
        await onDone(
          res.user.role !== user.role
            ? `แก้บัญชี ${res.user.username} แล้ว — สิทธิ์เปลี่ยนเป็น ${res.user.roleLabel} และ session เดิมถูกยกเลิก`
            : `แก้บัญชี ${res.user.username} แล้ว`,
        )
      } else {
        const res = await admins.create({
          username: username.trim(),
          displayName: displayName.trim(),
          email: email.trim(),
          empCode: empCode.trim(),
          role,
          password: ownPassword ? password : undefined,
        })
        onIssued?.(res)
        await onDone(`สร้างบัญชี ${res.user.username} แล้ว`)
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card form-card--wide">
      <h2>{editing ? `แก้ไขบัญชี ${user.username}` : 'สร้างบัญชีผู้ดูแล'}</h2>
      <form onSubmit={submit}>
        <div className="form-row">
          <label>
            ชื่อผู้ใช้
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="เช่น somchai.k"
              maxLength={64}
              disabled={editing}
              required={!editing}
            />
            <small className="hint">
              {editing
                ? 'แก้ไม่ได้ — ชื่อนี้ถูกบันทึกเป็นผู้ทำรายการไว้ในประวัติของตารางอื่นแล้ว'
                : 'a-z 0-9 จุด ขีดล่าง ขีดกลาง · ตั้งแล้วเปลี่ยนไม่ได้'}
            </small>
          </label>
          <label>
            ชื่อที่แสดง
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="เช่น สมชาย ก."
              maxLength={255}
            />
          </label>
        </div>

        <div className="form-row">
          <label>
            อีเมล
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
            />
          </label>
          <label>
            รหัสพนักงาน
            <input
              value={empCode}
              onChange={(e) => setEmpCode(e.target.value)}
              placeholder="เว้นไว้ได้"
              maxLength={32}
            />
            <small className="hint">ใส่ถ้าคนนี้เป็นพนักงานในระบบด้วย · บัญชีอย่าง it-support เว้นว่างได้</small>
          </label>
        </div>

        <label>
          สิทธิ์
          <select value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <small className="hint">{ROLE_NOTE[role]}</small>
        </label>

        {!editing && (
          <>
            <label className="check">
              <input
                type="checkbox"
                checked={ownPassword}
                onChange={(e) => setOwnPassword(e.target.checked)}
              />
              ตั้งรหัสผ่านเอง
            </label>
            {ownPassword ? (
              <label>
                รหัสผ่านชั่วคราว
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={minPassword}
                />
                {passwordBad && <small className="bad">ต้องยาวอย่างน้อย {minPassword} ตัวอักษร</small>}
                <small className="hint">
                  แสดงเป็นข้อความธรรมดาโดยตั้งใจ — คุณต้องอ่านมันไปบอกเจ้าตัวอยู่แล้ว
                  ช่องที่ซ่อนไว้มีแต่จะทำให้พิมพ์ผิดโดยไม่รู้ตัว
                </small>
              </label>
            ) : (
              <small className="hint">
                ระบบจะสุ่มรหัสชั่วคราวให้แล้วแสดงครั้งเดียวหลังกดสร้าง
                เจ้าตัวจะถูกบังคับให้เปลี่ยนตอนเข้าครั้งแรก
              </small>
            )}
          </>
        )}

        {error && <p className="error">{error}</p>}

        <div className="row-actions">
          <button type="submit" disabled={!canSubmit}>
            {busy ? 'กำลังบันทึก…' : editing ? 'บันทึก' : 'สร้างบัญชี'}
          </button>
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            ยกเลิก
          </button>
        </div>
      </form>
    </section>
  )
}
